import { FC, useEffect, useMemo, useState } from 'react'
import {
  useSafewords,
  useSecuritasIdentifiersFn,
  useUpdateDispatchNotes,
  useUpdateSafeword,
  useUpdateSecuritasIdentifiers,
  useUpdateTemplateId
} from '../../../../api/apiQueries'
import { Box, Button, Grid, IconButton, InputAdornment, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material'
import SafewordVisibilityOffIcon from '../../../../assets/images/video-access-ended.svg?react'
import SafewordVisibilityOnIcon from '../../../../assets/images/visibility-on.svg?react'
import EditIcon from '../../../../assets/images/edit-pencil.svg?react'
import { TemplateIDNames, TemplateIDNameToValueMap, TemplateIDNameValues } from '../locationManagementEnums'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { GBAddressCountry, GetSafewordsResponse, Location } from '../../../../api/models'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useAuthenticationContext } from '../../../../auth/AuthenticationContextProvider'
import {
  WRITE_LOCATION_DISPATCH_NOTES_SCOPE,
  WRITE_LOCATION_MSP_IDENTIFIERS_SCOPE,
  WRITE_LOCATION_SAFEWORD_SCOPE,
  WRITE_LOCATION_TEMPLATE_ID_SCOPE,
  READ_LOCATION_SAFEWORD_SCOPE
} from '../../../../constants'
import { Skeleton } from '../../../../components/Skeleton/Skeleton'
import { isProfessionallyMonitored } from './locationUtils'
import InfoOutlineIcon from '@mui/icons-material/InfoOutline'

interface AdditionalSettingsProps {
  location: Location
  templateId: string
  dispatchNotes: string
}

type AdditionalSettingsFormData = {
  safeword: string
  templateId: string
  siteNumber: number
  dispatchNotes: string
}

enum FieldNames {
  SAFEWORD = 'safeword',
  TEMPLATE_ID = 'templateId',
  SITE_NUMBER = 'siteNumber',
  DISPATCH_NOTES = 'dispatchNotes'
}

export const AdditionalSettings: FC<AdditionalSettingsProps> = ({ location, templateId, dispatchNotes }) => {
  const locationId = location.id
  const authContext = useAuthenticationContext()

  const getSafewordQuery = useSafewords(locationId, false)
  const updateSafewordMutation = useUpdateSafeword()
  const updateDispatchNotesMutation = useUpdateDispatchNotes(locationId)
  const updateTemplateIdMutation = useUpdateTemplateId(locationId)

  const isGBLocation = location?.address?.country === GBAddressCountry.GB

  const securitasIdentifiersQuery = useSecuritasIdentifiersFn()
  const updateSecuritasIdentifiersMutation = useUpdateSecuritasIdentifiers(locationId)
  const [siteNumber, setSiteNumber] = useState<number>(0)

  const isLoading =
    updateSafewordMutation.isPending ||
    updateDispatchNotesMutation.isPending ||
    updateTemplateIdMutation.isPending ||
    updateSecuritasIdentifiersMutation.isPending

  const [isEditable] = useState(true)
  const [isInSafewordEditMode, setIsInSafewordEditMode] = useState(false)
  const [isInTemplateIdEditMode, setIsInTemplateIdEditMode] = useState(false)
  const [isInSiteNumberEditMode, setIsInSiteNumberEditMode] = useState(false)
  const [isInDispatchNotesEditMode, setIsInDispatchNotesEditMode] = useState(false)
  const [safewordIsUnmasked, setSafewordIsUnmasked] = useState(false)
  const isInEditMode = isInSafewordEditMode || isInTemplateIdEditMode || isInSiteNumberEditMode || isInDispatchNotesEditMode
  const safewordsQuery = useSafewords(locationId, safewordIsUnmasked)
  const safeword = safewordsQuery.data?.safewords[0]?.value ?? ''
  const isSafewordLoading = safewordsQuery.isLoading || (safewordsQuery.isFetching && !safewordsQuery.data)
  const safewordDisplayValue = isSafewordLoading ? '' : safeword || 'Not Available'

  const { toast } = useSnackbar()

  const maxCharactersForDispatchNotes = 450
  const templateIdLabel = useMemo(() => {
    const matchingTemplate = TemplateIDNameValues.find(template => template.value === Number(templateId))
    return matchingTemplate?.name ?? templateId
  }, [templateId])

  const templateIdOptions = useMemo(() => {
    const options = Object.values(TemplateIDNames)
    return templateIdLabel && !options.includes(templateIdLabel as TemplateIDNames) ? [templateIdLabel, ...options] : options
  }, [templateIdLabel])

  useEffect(() => {
    const fetchSecuritasIdentifiers = async () => {
      if (isGBLocation) {
        const securitasIdentifiers = await securitasIdentifiersQuery(locationId)
        setSiteNumber(securitasIdentifiers.siteNumber ?? 0)
      }
    }
    fetchSecuritasIdentifiers()
  }, [locationId, securitasIdentifiersQuery, isGBLocation])

  const { handleSubmit, register, control, reset, setValue } = useForm<AdditionalSettingsFormData>({
    defaultValues: {
      safeword,
      templateId: templateIdLabel,
      siteNumber,
      dispatchNotes
    }
  })

  const SafewordVisibilityIcon = safewordIsUnmasked ? SafewordVisibilityOnIcon : SafewordVisibilityOffIcon
  const canReadSafeword = authContext.has(READ_LOCATION_SAFEWORD_SCOPE)

  const disabledFieldStyle = !isProfessionallyMonitored(location) ? { color: 'var(--text-disabled)' } : {}

  const settingsRowSx = {
    paddingBlock: 'var(--spacing-m)',
    borderBottom: '1px solid var(--border-medium)',
    alignItems: 'center'
  }
  const lastSettingsRowSx = {
    paddingBlock: 'var(--spacing-m)',
    alignItems: 'center'
  }
  const watchedDispatchNotes = useWatch({
    control,
    name: FieldNames.DISPATCH_NOTES,
    defaultValue: dispatchNotes
  })

  useEffect(() => {
    reset({
      safeword,
      templateId: templateIdLabel,
      siteNumber,
      dispatchNotes
    })
  }, [dispatchNotes, reset, safeword, siteNumber, templateIdLabel])

  const clearEditModes = () => {
    setIsInSafewordEditMode(false)
    setIsInTemplateIdEditMode(false)
    setIsInSiteNumberEditMode(false)
    setIsInDispatchNotesEditMode(false)
    reset({
      safeword,
      templateId: templateIdLabel,
      siteNumber,
      dispatchNotes
    })
  }

  const handleTemplateIdEdit = () => {
    setValue(FieldNames.TEMPLATE_ID, templateIdLabel, { shouldDirty: false })
    setIsInTemplateIdEditMode(true)
  }

  const getActiveEditMode = () => {
    if (isInSafewordEditMode) return FieldNames.SAFEWORD
    if (isInTemplateIdEditMode) return FieldNames.TEMPLATE_ID
    if (isInSiteNumberEditMode) return FieldNames.SITE_NUMBER
    if (isInDispatchNotesEditMode) return FieldNames.DISPATCH_NOTES
    return null
  }

  const handleSave = async (data: AdditionalSettingsFormData) => {
    if (!locationId) return

    switch (getActiveEditMode()) {
      case FieldNames.SAFEWORD: {
        const cachedSafewords = getSafewordQuery.data as GetSafewordsResponse | undefined
        const currentSafeword = cachedSafewords?.safewords[0]

        if (!cachedSafewords || !currentSafeword) {
          toast('Reveal safeword first, then save your update.', { variant: 'error' })
          return
        }

        if (data.safeword !== currentSafeword.value) {
          try {
            await updateSafewordMutation.mutateAsync({
              locationId,
              safewordId: currentSafeword.id,
              serial: cachedSafewords.serial,
              safeword: {
                name: currentSafeword.name,
                type: currentSafeword.type,
                value: data.safeword
              }
            })
            toast('Safe Word updated successfully.', { variant: 'success' })
          } catch {
            toast('Failed to update safe word.', { variant: 'error' })
            return
          }
        }
        break
      }

      case FieldNames.TEMPLATE_ID: {
        const selectedTemplateId = data.templateId

        if (!selectedTemplateId) {
          toast('Unable to update template ID.', { variant: 'error' })
          return
        }

        try {
          const selectedTemplateIdValue = TemplateIDNameToValueMap[selectedTemplateId as TemplateIDNames]
          await updateTemplateIdMutation.mutateAsync({ templateId: selectedTemplateIdValue })
          toast('Template ID updated successfully.', { variant: 'success' })
        } catch {
          toast('Failed to update template ID.', { variant: 'error' })
          return
        }
        break
      }

      case FieldNames.DISPATCH_NOTES: {
        try {
          await updateDispatchNotesMutation.mutateAsync({ dispatchNotes: data.dispatchNotes })
          toast('Dispatch notes updated successfully.', { variant: 'success' })
        } catch {
          toast('Failed to update dispatch notes.', { variant: 'error' })
          return
        }
        break
      }

      case FieldNames.SITE_NUMBER:
        try {
          await updateSecuritasIdentifiersMutation.mutateAsync({ siteNumber: data.siteNumber })
          toast('Site number updated successfully.', { variant: 'success' })
        } catch {
          toast('Failed to update site number.', { variant: 'error' })
          return
        }
        break

      case null:
      default:
        break
    }

    clearEditModes()
  }

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(handleSave)}
      direction="column"
      //spacing={'var(--spacing-m)'}
      sx={{
        backgroundColor: 'var(--bg-surface)',
        borderRadius: '0.5rem',
        paddingX: 'var(--spacing-m)',
        paddingBlockStart: 'var(--spacing-m)'
      }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant={'h2'} fontSize={'var(--size-xl)'} sx={{ fontWeight: '700', paddingBlockEnd: 'var(--spacing-s)' }}>
          Additional Settings
        </Typography>
        {isInEditMode && (
          <Stack direction="row" spacing={'var(--spacing-xs)'} sx={{ justifyContent: 'flex-end', padding: '0 1.5rem', position: 'sticky' }}>
            <Button
              data-testid="additional-settings-cancel-button"
              variant="outlined"
              onClick={clearEditModes}
              sx={{ height: 'var(--spacing-2xl)', fontSize: 'var(--size-s)' }}
            >
              Cancel
            </Button>
            <Button
              data-testid="additional-settings-save-button"
              variant="contained"
              type="submit"
              loading={isLoading}
              sx={{ height: 'var(--spacing-2xl)', fontSize: 'var(--size-s)' }}
            >
              Save
            </Button>
          </Stack>
        )}
      </Stack>
      <Grid container direction="row" sx={settingsRowSx}>
        <Grid size={3} sx={{ height: 'var(--spacing-3xl)', display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 'var(--size-s)', fontWeight: '700', ...disabledFieldStyle }}>Safe Word</Typography>
        </Grid>
        {isInSafewordEditMode ? (
          <Grid size={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              type="text"
              {...register(FieldNames.SAFEWORD)}
              label="Safe Word"
              placeholder={safewordIsUnmasked ? undefined : '•••••••••'}
              fullWidth
              sx={{
                '& .MuiInputBase-root': {
                  height: 'var(--spacing-3xl)'
                }
              }}
              slotProps={{
                inputLabel: {
                  shrink: true
                },
                input: {
                  endAdornment: canReadSafeword ? (
                    <InputAdornment position="end" sx={{ marginRight: 'var(--spacing-xs)' }}>
                      <IconButton
                        onClick={() => setSafewordIsUnmasked(prev => !prev)}
                        edge="end"
                        data-testid="additional-settings-safeword-visibility-toggle"
                      >
                        {safewordIsUnmasked ? (
                          <SafewordVisibilityOnIcon style={{ cursor: 'pointer', height: 'var(--spacing-m)', width: 'var(--spacing-m)' }} />
                        ) : (
                          <SafewordVisibilityOffIcon style={{ cursor: 'pointer', height: 'var(--spacing-m)', width: 'var(--spacing-m)' }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end" sx={{ marginRight: 'var(--spacing-xs)' }}>
                      <Tooltip title="User does not have permissions to read safe word" arrow>
                        <InfoOutlineIcon
                          data-testid="additional-settings-safeword-permission-info"
                          sx={{ height: 'var(--spacing-m)', width: 'var(--spacing-m)' }}
                        />
                      </Tooltip>
                    </InputAdornment>
                  )
                },
                htmlInput: {
                  className: safewordIsUnmasked ? undefined : 'large-dots',
                  ['data-testid']: 'additional-settings-safeword-input'
                }
              }}
            />
          </Grid>
        ) : (
          <>
            <Grid
              size={6}
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: 'var(--spacing-3xl)'
              }}
            >
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', maxWidth: '20.5rem', height: '100%' }}>
                <Typography
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    fontSize: 'var(--size-s)',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    paddingRight: 'calc(var(--spacing-m) + var(--spacing-s))',
                    ...disabledFieldStyle
                  }}
                >
                  <Skeleton isLoading={isSafewordLoading} width={'8rem'}>
                    {safewordIsUnmasked ? (
                      safewordDisplayValue
                    ) : (
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 'var(--size-s)', lineHeight: 1 }}>
                        *********
                      </Box>
                    )}
                  </Skeleton>
                </Typography>
                {canReadSafeword ? (
                  <SafewordVisibilityIcon
                    data-testid="additional-settings-safeword-display-visibility-toggle"
                    onClick={() => setSafewordIsUnmasked(prev => !prev)}
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      height: 'var(--spacing-m)',
                      width: 'var(--spacing-m)'
                    }}
                  />
                ) : (
                  <Tooltip title="User does not have permissions to read safe word" arrow>
                    <InfoOutlineIcon
                      data-testid="additional-settings-safeword-display-permission-info"
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        height: 'var(--spacing-m)',
                        width: 'var(--spacing-m)'
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Grid>
            <Grid size={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              {isProfessionallyMonitored(location) && !isInEditMode && authContext.has(WRITE_LOCATION_SAFEWORD_SCOPE) && (
                <Button
                  variant="text"
                  size="large"
                  disabled={!isEditable}
                  onClick={() => setIsInSafewordEditMode(true)}
                  data-testid="additional-settings-safeword-edit-button"
                  sx={{
                    height: 'var(--spacing-3xl)',
                    fontSize: 'var(--size-2xs)',
                    gap: 'var(--spacing-xs)'
                  }}
                >
                  <Typography>Edit</Typography>
                  <EditIcon />
                </Button>
              )}
            </Grid>
          </>
        )}
      </Grid>
      <Grid container direction="row" sx={settingsRowSx}>
        <Grid size={3} sx={{ height: 'var(--spacing-3xl)', display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 'var(--size-s)', fontWeight: '700', ...disabledFieldStyle }}>Template ID</Typography>
        </Grid>
        {isInTemplateIdEditMode ? (
          <Grid size={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Controller
              name={FieldNames.TEMPLATE_ID}
              control={control}
              render={({ field }) => (
                <TextField data-testid="additional-settings-template-id-select" fullWidth select label="Template ID" {...field}>
                  {templateIdOptions.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        ) : (
          <>
            <Grid size={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontSize: 'var(--size-s)', ...disabledFieldStyle }}>{templateIdLabel || 'Not Available'}</Typography>
            </Grid>
            <Grid size={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              {isProfessionallyMonitored(location) && !isInEditMode && authContext.has(WRITE_LOCATION_TEMPLATE_ID_SCOPE) && (
                <Button
                  variant="text"
                  size="large"
                  disabled={!isEditable}
                  onClick={handleTemplateIdEdit}
                  data-testid="additional-settings-template-id-edit-button"
                  sx={{
                    height: 'var(--spacing-3xl)',
                    fontSize: 'var(--size-2xs)',
                    gap: 'var(--spacing-xs)'
                  }}
                >
                  <Typography>Edit</Typography>
                  <EditIcon />
                </Button>
              )}
            </Grid>
          </>
        )}
      </Grid>
      {isGBLocation && (
        <Grid container direction="row" sx={settingsRowSx}>
          <Grid size={3} sx={{ height: 'var(--spacing-3xl)', display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 'var(--size-s)', fontWeight: '700', ...disabledFieldStyle }}>Site Number</Typography>
          </Grid>
          {isInSiteNumberEditMode ? (
            <Grid size={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                {...register(FieldNames.SITE_NUMBER)}
                label="Site Number"
                fullWidth
                slotProps={{
                  htmlInput: {
                    ['data-testid']: 'additional-settings-site-number-input'
                  }
                }}
              />
            </Grid>
          ) : (
            <>
              <Grid size={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 'var(--size-s)', ...disabledFieldStyle }}>{siteNumber === 0 ? 'Not Available' : siteNumber}</Typography>
              </Grid>
              <Grid size={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                {isProfessionallyMonitored(location) && !isInEditMode && authContext.has(WRITE_LOCATION_MSP_IDENTIFIERS_SCOPE) && (
                  <Button
                    variant="text"
                    size="large"
                    disabled={!isEditable}
                    onClick={() => setIsInSiteNumberEditMode(true)}
                    data-testid="additional-settings-site-number-edit-button"
                    sx={{
                      height: 'var(--spacing-3xl)',
                      fontSize: 'var(--size-2xs)',
                      gap: 'var(--spacing-xs)'
                    }}
                  >
                    <Typography>Edit</Typography>
                    <EditIcon />
                  </Button>
                )}
              </Grid>
            </>
          )}
        </Grid>
      )}
      <Grid container direction="row" sx={lastSettingsRowSx}>
        <Grid size={3} sx={{ height: 'var(--spacing-3xl)', display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 'var(--size-s)', fontWeight: '700', ...disabledFieldStyle }}>Dispatch Notes</Typography>
        </Grid>
        {isInDispatchNotesEditMode ? (
          <Grid size={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Stack direction="column" spacing={'var(--spacing-3xs)'} sx={{ width: '100%' }}>
              <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'flex-end', width: '100%' }}>
                <Box
                  sx={{
                    fontSize: 'var(--size-xs)',
                    fontWeight: '400',
                    color: 'var(--text-secondary)',
                    textAlign: 'right'
                  }}
                >
                  {watchedDispatchNotes.length} / {maxCharactersForDispatchNotes}
                </Box>
              </Stack>
              <TextField
                id="dispatch-notes"
                {...register(FieldNames.DISPATCH_NOTES)}
                label="Dispatch Notes"
                placeholder={'Input dispatch notes here'}
                autoComplete={'off'}
                multiline
                rows={4}
                fullWidth
                slotProps={{
                  htmlInput: {
                    maxLength: 450,
                    ['data-testid']: 'additional-settings-dispatch-notes-input'
                  }
                }}
              />
            </Stack>
          </Grid>
        ) : (
          <>
            <Grid size={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontSize: 'var(--size-s)', ...disabledFieldStyle }}>{dispatchNotes}</Typography>
            </Grid>
            <Grid size={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              {isProfessionallyMonitored(location) && !isInEditMode && authContext.has(WRITE_LOCATION_DISPATCH_NOTES_SCOPE) && (
                <Button
                  variant="text"
                  size="large"
                  disabled={!isEditable}
                  onClick={() => setIsInDispatchNotesEditMode(true)}
                  data-testid="additional-settings-dispatch-notes-edit-button"
                  sx={{
                    height: 'var(--spacing-3xl)',
                    fontSize: 'var(--size-2xs)',
                    gap: 'var(--spacing-xs)'
                  }}
                >
                  <Typography>Edit</Typography>
                  <EditIcon />
                </Button>
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Stack>
  )
}
